import { defaultColorMapFromString, isSegmented } from "./ColorMap";
import { hasIdxUnit } from "./ColorMapStepsRequest";
const NUMBER_RE = /^-?\d+(?:\.\d+)?(?:e[+-]?\d+)?$/i;
const HEX_RE = /^#?[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/;
const DEFAULT_COLOR_MAP_STEPS_SIMPLIFY_OPTIONS = {
    epsilon: 0.015,
    maxStops: 16,
};
const detectDelimiter = (line) => {
    if (line.includes(";")) {
        return ";";
    }
    if (line.includes(",")) {
        return ",";
    }
    return ";";
};
const splitLine = (line, delimiter) => line
    .split(delimiter)
    .map((cell) => cell.trim())
    .filter((cell) => cell !== "");
const isNumeric = (value) => NUMBER_RE.test(value.trim());
const normalizeHex = (value) => {
    const trimmed = value.trim();
    const hex = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
    return hex.toLowerCase();
};
const hexPairToUnit = (value) => Number.parseInt(value, 16) / 255;
const parseHexToRgba = (value) => {
    const normalized = normalizeHex(value).replace("#", "");
    const r = hexPairToUnit(normalized.slice(0, 2));
    const g = hexPairToUnit(normalized.slice(2, 4));
    const b = hexPairToUnit(normalized.slice(4, 6));
    const a = normalized.length === 8 ? hexPairToUnit(normalized.slice(6, 8)) : 1;
    return { r, g, b, a };
};
const srgbToLinear = (c) => {
    if (c <= 0.04045) {
        return c / 12.92;
    }
    return Math.pow((c + 0.055) / 1.055, 2.4);
};
const rgbToOklab = (r, g, b) => {
    const lr = srgbToLinear(r);
    const lg = srgbToLinear(g);
    const lb = srgbToLinear(b);
    const l = 0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb;
    const m = 0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb;
    const s = 0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb;
    const l_ = Math.cbrt(l);
    const m_ = Math.cbrt(m);
    const s_ = Math.cbrt(s);
    return {
        L: 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_,
        a: 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_,
        b: 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_,
    };
};
const toOklabSample = (sample) => {
    const { r, g, b, a } = parseHexToRgba(sample.color);
    const lab = rgbToOklab(r, g, b);
    return {
        value: sample.value,
        L: lab.L,
        a: lab.a,
        b: lab.b,
        alpha: a,
        hex: normalizeHex(sample.color),
    };
};
const lerp = (a, b, t) => a + (b - a) * t;
const interpolateSample = (start, end, t) => ({
    L: lerp(start.L, end.L, t),
    a: lerp(start.a, end.a, t),
    b: lerp(start.b, end.b, t),
    alpha: lerp(start.alpha, end.alpha, t),
});
const colorError = (expected, actual, includeAlpha, alphaWeight) => {
    const dL = actual.L - expected.L;
    const da = actual.a - expected.a;
    const db = actual.b - expected.b;
    let sum = dL * dL + da * da + db * db;
    if (includeAlpha) {
        const dAlpha = (actual.alpha - expected.alpha) * alphaWeight;
        sum += dAlpha * dAlpha;
    }
    return Math.sqrt(sum);
};
const simplifyWithEpsilon = (samples, epsilon, includeAlpha, alphaWeight) => {
    if (samples.length <= 2 || epsilon <= 0) {
        return samples;
    }
    const keep = new Array(samples.length).fill(false);
    keep[0] = true;
    keep[samples.length - 1] = true;
    const stack = [[0, samples.length - 1]];
    while (stack.length > 0) {
        const [start, end] = stack.pop();
        const startSample = samples[start];
        const endSample = samples[end];
        const span = endSample.value - startSample.value;
        let maxError = -1;
        let maxIndex = -1;
        for (let i = start + 1; i < end; i++) {
            const sample = samples[i];
            const t = span === 0 ? 0 : (sample.value - startSample.value) / span;
            const expected = interpolateSample(startSample, endSample, t);
            const err = colorError(expected, sample, includeAlpha, alphaWeight);
            if (err > maxError) {
                maxError = err;
                maxIndex = i;
            }
        }
        if (maxIndex !== -1 && maxError > epsilon) {
            keep[maxIndex] = true;
            stack.push([start, maxIndex], [maxIndex, end]);
        }
    }
    const simplified = [];
    for (let i = 0; i < samples.length; i++) {
        if (keep[i]) {
            simplified.push(samples[i]);
        }
    }
    return simplified;
};
const roundToDecimals = (value, decimals) => {
    if (!Number.isFinite(value)) {
        return value;
    }
    const factor = Math.pow(10, decimals);
    const adjusted = value * factor;
    const rounded = Math.round(adjusted + (adjusted >= 0 ? Number.EPSILON : -Number.EPSILON));
    const result = rounded / factor;
    return Object.is(result, -0) ? 0 : result;
};
const applyStopPrecision = (input) => {
    if (!Array.isArray(input) || input.length === 0) {
        return input;
    }
    const maxValue = input.reduce((max, [value]) => (value > max ? value : max), -Infinity);
    if (!Number.isFinite(maxValue)) {
        return input;
    }
    const decimals = maxValue >= 20 ? 0 : maxValue <= 2 ? 3 : 1;
    return input.map(([value, color]) => [roundToDecimals(value, decimals), color]);
};
const simplifyStops = (stops, options) => {
    if (!Array.isArray(stops)) {
        return stops;
    }
    if (stops.length <= 2) {
        return applyStopPrecision(stops);
    }
    const rawEpsilon = options?.epsilon ?? 0.01;
    const epsilon = Number.isFinite(rawEpsilon) ? rawEpsilon : 0.01;
    const includeAlpha = options?.includeAlpha ?? false;
    const alphaWeight = options?.alphaWeight ?? 1;
    const maxStops = options?.maxStops ?? null;
    const samples = stops
        .slice()
        .sort((a, b) => a[0] - b[0])
        .map(([value, color]) => toOklabSample({ value, color }));
    const run = (eps) => simplifyWithEpsilon(samples, eps, includeAlpha, alphaWeight).map((sample) => [sample.value, sample.hex]);
    let simplified = run(epsilon);
    if (maxStops != null && Number.isFinite(maxStops) && maxStops >= 2 && simplified.length > maxStops) {
        let low = Math.max(epsilon, 0);
        let high = Math.max(epsilon, 1e-6);
        let candidate = run(high);
        while (candidate.length > maxStops && high < 1) {
            high *= 2;
            candidate = run(high);
        }
        if (candidate.length <= maxStops) {
            for (let i = 0; i < 16; i++) {
                const mid = (low + high) / 2;
                const midResult = run(mid);
                if (midResult.length > maxStops) {
                    low = mid;
                }
                else {
                    high = mid;
                    candidate = midResult;
                }
            }
            simplified = candidate;
        }
        else {
            simplified = candidate;
        }
    }
    return applyStopPrecision(simplified);
};
const parseSample = (cells, lineNumber) => {
    if (cells.length < 2) {
        throw new Error(`'${cells.join(";")}' not a valid sample at line ${lineNumber}`);
    }
    const rawValue = cells[0]?.trim();
    const rawColor = cells[1]?.trim();
    if (!rawValue || !isNumeric(rawValue)) {
        throw new Error(`'${cells.join(";")}' not a valid sample at line ${lineNumber}`);
    }
    if (!rawColor || !HEX_RE.test(rawColor)) {
        throw new Error(`'${cells.join(";")}' not a valid sample at line ${lineNumber}`);
    }
    return {
        value: Number(rawValue),
        color: normalizeHex(rawColor),
    };
};
const hasHeaderRow = (cells) => {
    const value = cells[0]?.trim();
    const color = cells[1]?.trim();
    if (!value || !color) {
        return true;
    }
    if (!isNumeric(value)) {
        return true;
    }
    if (!HEX_RE.test(color)) {
        return true;
    }
    return false;
};
export class ColorMapStepsResponse {
    constructor(stops, segmented = false, exactValues = false) {
        this.stops = stops;
        this.segmented = segmented;
        this.exactValues = exactValues;
    }
    static simplifyStops(stops, options) {
        return simplifyStops(stops, options);
    }
    static applyStopPrecision(stops) {
        return applyStopPrecision(stops);
    }
    static parseFromCsvForRequest(txt, request) {
        const response = ColorMapStepsResponse.parseFromCsv(txt);
        const isIdx = hasIdxUnit(request.parameter);
        const style = request.style ?? defaultColorMapFromString(String(request.parameter));
        const segmented = isIdx || isSegmented(style);
        if (isIdx) {
            return new ColorMapStepsResponse(response.stops, segmented, true);
        }
        return new ColorMapStepsResponse(simplifyStops(response.stops, DEFAULT_COLOR_MAP_STEPS_SIMPLIFY_OPTIONS), segmented);
    }
    static parseFromCsv(txt) {
        const lines = txt
            .split(/\r?\n/)
            .map((line) => line.trim())
            .filter((line) => line !== "");
        if (lines.length === 0) {
            throw new Error("empty CSV");
        }
        const delimiter = detectDelimiter(lines[0]);
        const firstCells = splitLine(lines[0], delimiter);
        const startIndex = hasHeaderRow(firstCells) ? 1 : 0;
        const samples = [];
        for (let i = startIndex; i < lines.length; i++) {
            const cells = splitLine(lines[i], delimiter);
            if (cells.length === 0) {
                continue;
            }
            samples.push(parseSample(cells, i));
        }
        if (samples.length === 0) {
            throw new Error("CSV contained no colormap samples");
        }
        samples.sort((a, b) => a.value - b.value);
        const stops = [];
        let lastColor = null;
        for (const sample of samples) {
            if (lastColor === null || sample.color !== lastColor) {
                stops.push([sample.value, sample.color]);
                lastColor = sample.color;
            }
        }
        const lastSample = samples[samples.length - 1];
        const lastStop = stops[stops.length - 1];
        if (lastStop[0] !== lastSample.value) {
            stops.push([lastSample.value, lastSample.color]);
        }
        return new ColorMapStepsResponse(stops);
    }
    simplify(options) {
        if (this.exactValues) {
            return new ColorMapStepsResponse(this.stops, this.segmented, this.exactValues);
        }
        return new ColorMapStepsResponse(simplifyStops(this.stops, options), this.segmented, this.exactValues);
    }
    toTransferable() {
        return {
            stops: this.stops,
            segmented: this.segmented,
            exactValues: this.exactValues,
        };
    }
    static fromTransferable(v) {
        return new ColorMapStepsResponse(v.stops, v.segmented ?? false, v.exactValues ?? false);
    }
}
//# sourceMappingURL=ColorMapStepsResponse.js.map